import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { AnswerSelection, QuestionItem, TeamNode } from '../components/FilterDrawer';

const TEAMS_PARAM = 'unit-teams';
const ANSWERS_PARAM = 'answers';

const collectTeamIds = (teams: TeamNode[]): string[] =>
  teams.flatMap((team) => [team.id, ...collectTeamIds(team.children)]);

const parseTeamsFromUrl = (search: string, teams?: TeamNode[]): Set<string> => {
  const value = new URLSearchParams(search).get(TEAMS_PARAM);
  if (!value) {
    return new Set();
  }
  const validTeamIds = teams ? new Set(collectTeamIds(teams)) : undefined;
  return new Set(value.split(',').filter((id) => id && (!validTeamIds || validTeamIds.has(id))));
};

const serializeAnswer = ({ questionId, optionId, statementId }: AnswerSelection): string =>
  statementId ? `${questionId}:${statementId}:${optionId}` : `${questionId}:${optionId}`;

const parseAnswer = (value: string): AnswerSelection | null => {
  const parts = value.split(':');
  if (parts.length === 2) {
    const [questionId, optionId] = parts;
    if (!questionId || !optionId) {
      return null;
    }
    return { questionId, optionId };
  }

  if (parts.length >= 3) {
    const questionId = parts[0];
    const optionId = parts[parts.length - 1];
    const statementId = parts.slice(1, -1).join(':');
    if (!questionId || !optionId || !statementId) {
      return null;
    }
    return { questionId, optionId, statementId };
  }

  return null;
};

const parseAnswersFromUrl = (search: string, questions?: QuestionItem[]): AnswerSelection[] => {
  const value = new URLSearchParams(search).get(ANSWERS_PARAM);
  if (!value) {
    return [];
  }

  const validQuestions = questions ? new Map(questions.map((question) => [question.id, question])) : undefined;
  return value
    .split(',')
    .filter(Boolean)
    .map(parseAnswer)
    .filter(
      (answer): answer is AnswerSelection =>
        answer !== null &&
        (!validQuestions ||
          (() => {
            const question = validQuestions.get(answer.questionId);
            return (
              !!question &&
              question.options.some((option) => option.id === answer.optionId) &&
              (answer.statementId === undefined ||
                !!question.statements?.some((statement) => statement.id === answer.statementId))
            );
          })()),
    );
};

const updateSearchParams = (search: string, teams: Set<string>, answers: AnswerSelection[]): string => {
  const params = new URLSearchParams(search);

  if (teams.size > 0) {
    params.set(TEAMS_PARAM, Array.from(teams).join(','));
  } else {
    params.delete(TEAMS_PARAM);
  }

  if (answers.length > 0) {
    params.set(ANSWERS_PARAM, answers.map(serializeAnswer).join(','));
  } else {
    params.delete(ANSWERS_PARAM);
  }

  return params.toString();
};

export const useReportFiltersUrl = (availableTeams?: TeamNode[], availableQuestions?: QuestionItem[]) => {
  const history = useHistory();
  const location = useLocation();

  const selectedTeams = useMemo(
    () => parseTeamsFromUrl(location.search, availableTeams),
    [location.search, availableTeams],
  );
  const selectedAnswers = useMemo(
    () => parseAnswersFromUrl(location.search, availableQuestions),
    [location.search, availableQuestions],
  );

  const pushFiltersToUrl = useCallback(
    (teams: Set<string>, answers: AnswerSelection[]) => {
      const nextSearch = updateSearchParams(location.search, teams, answers);
      if (nextSearch === new URLSearchParams(location.search).toString()) {
        return;
      }

      history.push({
        pathname: location.pathname,
        search: nextSearch,
      });
    },
    [history, location.pathname, location.search],
  );

  const setSelectedTeams = useCallback(
    (teams: Set<string>) => {
      pushFiltersToUrl(teams, parseAnswersFromUrl(location.search, availableQuestions));
    },
    [availableQuestions, location.search, pushFiltersToUrl],
  );

  const setSelectedAnswers = useCallback(
    (answers: AnswerSelection[]) => {
      pushFiltersToUrl(parseTeamsFromUrl(location.search, availableTeams), answers);
    },
    [availableTeams, location.search, pushFiltersToUrl],
  );

  return {
    selectedTeams,
    selectedAnswers,
    setSelectedTeams,
    setSelectedAnswers,
  };
};
